import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { visuallyHidden } from '@mui/utils';
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import { formatFloatToReal } from "../../common/utils";
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { sumPreReceivedValues, sumReceivedValues } from "../../common/utils";
import { useSales } from '../../apis/sales';

const headCells = [
    {
        id: 'code',
        numeric: false,
        disablePadding: false,
        label: 'Codigo',
    },
    {
        id: 'razaoSocial',
        numeric: false,
        disablePadding: false,
        label: 'Cliente',
    },
    {
        id: 'totalDolar',
        numeric: false,
        disablePadding: false,
        label: 'Total',
    },
    {
        id: 'totalQuitado',
        numeric: false,
        disablePadding: false,
        label: 'Quitado',
    },
    {
        id: 'totalEmAberto',
        numeric: false,
        disablePadding: false,
        label: 'Pendente',
    },
    {
        id: 'preReceiving',
        numeric: false,
        disablePadding: false,
        label: 'Pré-Recebimento',
    },
    {
        id: 'dataVenda',
        numeric: false,
        disablePadding: false,
        label: 'Data Venda',
    },
    {
        id: 'creationDate',
        numeric: false,
        disablePadding: false,
        label: 'Data Criação',
    },
    {
        id: 'status',
        numeric: false,
        disablePadding: false,
        label: 'Status',
    },
    {
        id: 'actions',
        numeric: false,
        disablePadding: false,
        label: 'Ações',
    }
];

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } =
        props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                </TableCell>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        //sortDirection={orderBy === headCell.id ? order : false}
                        sx={{ fontSize: '12px', fontWeight: 'bold' }}
                    >
                        {headCell.label}
                        {/*<TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null} 
                        </TableSortLabel> */}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    // numSelected: PropTypes.number.isRequired,
    //onRequestSort: PropTypes.func.isRequired,
    // onSelectAllClick: PropTypes.func.isRequired,
    //order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    //orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
    const { numSelected } = props;

    return (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(numSelected > 0 && {
                    bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                }),
            }}
        >
        </Toolbar>
    );
}

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};

const SaleTable = props => {
    const { setLoadingData, field, priority, searchContent, mapFields } = props
    const [order, setOrder] = useState('desc');
    const [saleList, setSaleList] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [orderBy, setOrderBy] = useState('creationDate');
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const navigate = useNavigate();

    const { data, isLoading, isError, error, fetchNextPage, isFetching, refetch } = useSales(rowsPerPage, priority == "open", mapFields[field], searchContent, priority == "closed");

    useEffect(() => {
        const loadingStatus = isLoading || isFetching;
        setLoadingData(loadingStatus);
    
        if (!data) return;
    
        const allSales = data.pages.flatMap(page => page.content || []);

        setTotalElements(data.pages[0].totalElements);

        sumAllSalesValues(allSales);
    
        setSaleList(allSales);
    
        if (isError) {
            console.error("Erro ao carregar dados:", error);
        }
    }, [data, isLoading, isError, error, isFetching]);

    useEffect(() => {
        refetch();
        setPage(0);
    }, [searchContent, priority]);


    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const goToSale = (id) => {
        navigate(`/sales/${id}`, { id: id });
    }

    const handleClick = (id) => {
        goToSale(id);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
        fetchNextPage()
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        refetch();
    };

    const sumAllSalesValues = (sales) => {
        let totalValue = 0;
        let totalReceived = 0;
        sales.forEach(item => {
            let totalReceivedItem = sumReceivedValues(item.recebimentos);
            totalValue += item.totalDolar;
            totalReceived += totalReceivedItem;

            item.totalQuitado = totalReceivedItem;
            item.totalEmAberto = item.totalDolar - totalReceivedItem;
            item.totalPreRecebimento = sumPreReceivedValues(item.preRecebimento) - sumReceivedValues(item.preRecebimento);
        });
        return [totalValue - totalReceived, totalValue, totalReceived];
    }

    const isSelected = (id) => selected.indexOf(id) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - saleList.length) : 0;

    return (
        <Box sx={{ width: '100%' }}>
            <Card variant="outlined" sx={{ borderRadius: '10px' }}>
                <CardContent>
                    <Grid container alignItems="center">
                        <TableContainer>
                            <Table
                                sx={{ minWidth: 750 }}
                                aria-labelledby="tableTitle"
                                size='small'
                            >
                                <EnhancedTableHead
                                    // numSelected={selected.length}
                                    //order={order}
                                    //orderBy={orderBy}
                                    // onSelectAllClick={handleSelectAllClick}
                                    //onRequestSort={handleRequestSort}
                                    rowCount={saleList.length}
                                />
                                <TableBody>
                                    {saleList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => {
                                            const isItemSelected = isSelected(row.id);
                                            const labelId = `enhanced-table-checkbox-${index}`;

                                            return (
                                                <TableRow
                                                    hover
                                                    aria-checked={isItemSelected}
                                                    tabIndex={-1}
                                                    key={row.id}
                                                // style={{ height: '60px' }}
                                                >
                                                    <TableCell>
                                                    </TableCell>
                                                    <TableCell style={{ width: '12%', fontSize: '11px' }} align="left" padding="normal">KAV-{row.code}</TableCell>
                                                    <TableCell style={{ width: '14%', fontSize: '11px' }} align="left" padding="normal">{row.importadora.razaoSocial}</TableCell>
                                                    <TableCell style={{ width: '12%', fontSize: '11px' }} align="left" padding="normal">{formatFloatToReal(row.totalDolar)}</TableCell>
                                                    <TableCell style={{ width: '12%', fontSize: '11px' }} align="left" padding="normal">{formatFloatToReal(row.totalQuitado)}</TableCell>
                                                    <TableCell style={{ width: '12%', fontSize: '11px' }} align="left" padding="normal">{formatFloatToReal(row.totalEmAberto)}</TableCell>
                                                    <TableCell style={{ width: '14%', fontSize: '11px' }} align="left" padding="normal">{formatFloatToReal(row.totalPreRecebimento, 2)}</TableCell>
                                                    {/* <TableCell style={{ width: '15%', fontSize: '12px' }} align="left" padding="normal">{row.taxaJuros}% A.A</TableCell> */}
                                                    <TableCell style={{ width: '12%', fontSize: '11px' }} align="left" padding="normal">{dayjs(row.dataVenda).format('DD/MM/YYYY').toString()}</TableCell>
                                                    <TableCell style={{ width: '12%', fontSize: '11px' }} align="left" padding="normal">{dayjs(row.creationDate).format('DD/MM/YYYY').toString()}</TableCell>
                                                    {/* <TableCell style={{ width: '15%', fontSize: '12px' }} align="left" padding="normal">{dayjs(row.dataLiquidacaoContrato).format('DD/MM/YYYY').toString()}</TableCell> */}
                                                    <TableCell style={{ width: '10%', fontSize: '11px' }} align="left" padding="normal">
                                                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                            <CircularProgress variant="determinate" color={(row.totalQuitado / row.totalDolar) * 100 >= 80 ? "success" : "warning"} value={(row.totalQuitado / row.totalDolar) * 100} />
                                                            <Box
                                                                sx={{
                                                                    top: 0,
                                                                    left: 0,
                                                                    bottom: 0,
                                                                    right: 0,
                                                                    position: 'absolute',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <Typography variant="caption" component="div" color="text.primary" style={{ fontSize: "8px" }}>
                                                                    {`${Math.round((row.totalQuitado / row.totalDolar) * 100)}%`}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell style={{ width: '15%' }} align="left" padding="normal">
                                                        <IconButton
                                                            onClick={(event) => handleClick(row.id)}
                                                            size='small'>
                                                            <VisibilityIcon fontSize='small' />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    {emptyRows > 0 && (
                                        <TableRow
                                            style={{
                                                height: 33 * emptyRows,
                                            }}
                                        >
                                            <TableCell colSpan={6} />
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[25, 50, 75, 100]}
                            component="div"
                            count={totalElements}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};

export default SaleTable;